import { useState, useEffect } from "react";
import { useStockControlStore } from "../hooks/useStockControlStore";
import { useLoading } from "../hooks/useLoading";
import inventario from "../api/inventario";
import { useDepositosStore } from "../hooks/useDepositosStore";
import { useNavigate } from "react-router";

export const useControlStockSelectivo = () => {
  const {
    setArticuloActual,
    setStockReal,
    agregarArticuloControlado,
    articuloActual,
    stockReal,
    setMsgError,
    setMensajeComprobante,
    resetFormulario,
    resetArticulosControlados,
    setArticulosDisponibles,
    articulosDisponibles,
    articulosControlados,
  } = useStockControlStore();

  const idEmpleado = JSON.parse(localStorage.getItem("idEmpleado")) || null;
  const { depositoSeleccionado } = useDepositosStore();
  const navigate = useNavigate();
  const [articulosSeleccionados, setArticulosSeleccionados] = useState([]);
  const { loading, withLoading } = useLoading();

  useEffect(() => {
    if (!depositoSeleccionado) return;

    const cargarArticulosDisponibles = async () => {
      try {
        const rawLista = await withLoading(async () => {
          return await inventario.getListaArticulosIMA(depositoSeleccionado.idDeposito);
        });

        if (!Array.isArray(rawLista)) {
          throw new Error("La respuesta de la API no es una lista válida.");
        }

        const lista = rawLista.map(item => ({
          idArticulo: item.idArticulo,
          descripcion: item.descripcion,
        }));

        setArticulosDisponibles(lista);
      } catch (error) {
        console.error(" Error al cargar artículos selectivos", error);
        setMsgError("No se pudieron cargar los artículos disponibles.");
      }
    };

    // Reinicia estados cuando cambia el depósito
    resetArticulosControlados();
    resetFormulario();
    setMensajeComprobante("");
    setArticuloActual(null);
    setStockReal(null);
    setMsgError("");

    cargarArticulosDisponibles();
  }, [depositoSeleccionado]);

  const seleccionarArticulo = async idArticulo => {
    if (!depositoSeleccionado) {
      setMsgError("Depósito no definido.");
      return;
    }

    try {
      await withLoading(async () => {
        const detalle = await inventario.getArticuloIMAPorId(idArticulo);
        const fecha = new Date().toISOString().split("T")[0];
        const stockTeorico = await inventario.getStockTeoricoIMA(idArticulo, depositoSeleccionado.idDeposito, fecha);

        const articulo = {
          idArticulo: detalle.idArticulo,
          descripcion: detalle.descripcion,
          codigo: detalle.codigo,
          codigoBarra: detalle.codigoBarra,
          ubicacion: detalle.ubicacion,
          stockTeorico,
          stockReal: 0,
        };

        setArticuloActual(articulo);
        setStockReal(null);
      });
    } catch (error) {
      console.error(" Error al obtener artículo", error);
      setMsgError("No se pudo obtener el artículo seleccionado.");
    }
  };

  const guardarYResetear = async () => {
    if (!articuloActual || stockReal === null || isNaN(stockReal)) {
      setMsgError("Falta el stock real.");
      return;
    }

    // Guardar el artículo controlado
    agregarArticuloControlado({ ...articuloActual, stockReal });

    // Marcar el artículo como controlado
    setArticulosSeleccionados(prev => [...prev, articuloActual.idArticulo]);

    // Limpiar el formulario y resetear el artículo actual
    resetFormulario();
    setArticuloActual(null);

    // Calcular cuántos artículos quedan disponibles
    const articulosRestantes = articulosDisponibles.filter(a => !articulosSeleccionados.includes(a.idArticulo) && a.idArticulo !== articuloActual.idArticulo);

    // Si no quedan más artículos → ejecutar ajuste
    if (articulosRestantes.length === 0) {
      await finalizarControl();
    }
  };

  const finalizarControl = async () => {
    if (!idEmpleado || !depositoSeleccionado?.idDeposito) {
      setMsgError("Faltan datos de usuario o del depósito.");
      return;
    }

    if (articulosControlados.length === 0) {
      setMensajeComprobante("No se controló ningún artículo durante el ciclo de inventario.\n\nControl finalizado sin movimientos registrados.");
      setTimeout(() => {
        navigate(-1);
      }, 3000);
      return;
    }

    try {
      const movimientos = articulosControlados.map(a => ({
        idArticulo: a.idArticulo,
        cantidad: a.stockReal,
        stockReal: a.stockReal,
        stockTeorico: a.stockTeorico,
      }));

      const comprobante = await withLoading(async () => {
        return await inventario.persistirAjustes(idEmpleado, depositoSeleccionado.idDeposito, movimientos);
      });

      if (comprobante) {
        setMensajeComprobante(`Se guardaron los ajustes por inventario correspondientes.\n\nComprobante Nº\n${comprobante}`);
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }
    } catch (error) {
      console.error(" Error al guardar ajuste final", error);
      setMsgError("No se pudo guardar el ajuste final.");
    }
  };

  return {
    seleccionarArticulo,
    guardarYResetear,
    finalizarControl,
    articulosSeleccionados,
    articulosDisponibles,
    loading,
  };
};
